import { useState } from "react";

import { useUser } from "../useUser";
import { analytics } from "../../Analytics";

const ENDPOINT = process.env.REACT_APP_STRIPE_ENDPOINT;

export type TPromoRedemptionStatus = "idle" | "loading" | "success" | "error";

export const usePromoCodeRedemption = (location: string) => {
  const { allstarUser } = useUser();
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<TPromoRedemptionStatus>("idle");
  const [message, setMessage] = useState("");

  // Mobile IAP subscriptions are managed in the originating store — Stripe
  // promo codes can't apply to them even though the provider reads STRIPE.
  const isMobileIapUser =
    allstarUser?.user?.usingAppStoreInAppPurchases === true ||
    allstarUser?.user?.usingPlayStoreInAppPurchases === true;

  const canRedeem =
    allstarUser?.user?.credits?.providerName === "STRIPE" && !isMobileIapUser;

  const reset = () => {
    setCode("");
    setStatus("idle");
    setMessage("");
  };

  const applyCode = async () => {
    if (!code.trim() || status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch(`${ENDPOINT}/stripe/v1/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${allstarUser.token}`,
        },
        body: JSON.stringify({ promoCode: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Only surface backend messages meant for users. An endpoint that
        // predates promo support falls through to its generic body
        // validation ("Failed to supply lookup") — never show that.
        const backendMessage =
          typeof data.message === "string" &&
          !data.message.toLowerCase().includes("lookup")
            ? data.message
            : "";
        setMessage(
          backendMessage || "We couldn't apply that code. Please try again.",
        );
        setStatus("error");
        return;
      }
      analytics.track("Promo Code Redeemed", {
        code: data.applied?.code,
        percentOff: data.applied?.percentOff,
        location,
      });
      const nextBill = data.applied?.nextBillingDate
        ? new Date(data.applied.nextBillingDate).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })
        : null;
      setMessage(
        `${data.applied?.percentOff}% off applied — starts on your next bill${
          nextBill ? ` on ${nextBill}` : ""
        }`,
      );
      setStatus("success");
    } catch {
      setMessage("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return { code, setCode, status, message, applyCode, canRedeem, reset };
};
